import { withTheme } from 'styled-components';
import TranslateIgnoreText from '@this/shared/text/translate_ignore_text';
import { toDomesticTel } from '@this/src/util';
import MarketLogHelper from '@this/domain/market_log/market_log_helper';
import {
  buildShinkansenTicketPickupNotice,
  shouldShowShinkansenTicketPickupNotice
} from '@this/domain/shinkansen_ticket_pickup_notice';
import {
  OVERSEAS_24H_SUPPORT_NOTICE_BODY_LINES,
  OVERSEAS_24H_SUPPORT_NOTICE_TITLE,
  shouldShowOverseas24hSupportNotice
} from '@this/domain/overseas_24h_support_notice';
import {
  buildBusinessEkinetTicketlessNotice,
  hasBusinessEkinetExpressTransport,
  hasBusinessEkinetTransport
} from '@this/domain/business_ekinet_ticketless_notice';
import CoreAppContext from '@this/components/core_app/core_app_context';
import type Trip from '@this/domain/trip/trip2';
import A from '@this/shared/atoms/a';
import type TransportElementAttachFile from '@this/domain/transport_element_attach_file';
import PdfIcon from '@material-ui/icons/PictureAsPdf';
import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/shared/atoms/media';
import type User from '@this/domain/user/user';
import TripCardHotelTypeLine from '@this/components/trips_management/trip_detail/trip_card_hotel_type_line';
import Box from '@material-ui/core/Box';
import ExternalLinkIcon from '@this/shared/icons/external_link_icon';
import { Link } from '@this/shared/ui/navigations/link';
import type OrderItem from '@this/domain/order_item2';
import type TravelerInformation from '@this/domain/traveler_information';
import HotelElement from '@this/domain/hotel_element2';
import TransportElement from '@this/domain/transport_element2';
import WifiElement from '@this/domain/wifi_element/wifi_element2';

type Props = {
  user: User;
  trip: Trip;
  orderItem: OrderItem;
  serviceId: number;
  theme: Theme;
  hideCancelPolicy?: boolean;
};

interface TranslatedLine {
  label: string;
  value: string;
  translate: boolean;
}

interface Theme {
  themeClass: string;
  serviceName: string;
}

const OrderItemContent: React.FC<Props> = ({ user, trip, orderItem, serviceId, theme, hideCancelPolicy }) => {
  const { availableOptions } = React.useContext(CoreAppContext);
  const shinkansenTicketPickupNotice = buildShinkansenTicketPickupNotice(
    orderItem.ticketingMethod,
    orderItem.pickupStation,
    serviceId
  );
  const showOverseas24hSupportNotice = shouldShowOverseas24hSupportNotice(
    serviceId,
    availableOptions,
    orderItem.orderItemCategory
  );
  const showBusinessEkinetTicketlessNotice = hasBusinessEkinetTransport(orderItem.transports);
  const businessEkinetTicketlessNotice = showBusinessEkinetTicketlessNotice
    ? buildBusinessEkinetTicketlessNotice(hasBusinessEkinetExpressTransport(orderItem.transports))
    : null;
  const styles = getStyles();

  const renderPickupStationLabel = () => {
    const labelStyle = { color: 'red', fontWeight: 'bold' as const, textDecoration: 'underline' as const };
    if (shinkansenTicketPickupNotice?.stationLabelUrl) {
      return (
        <Link
          href={shinkansenTicketPickupNotice.stationLabelUrl}
          target="_blank"
          rel="noopener noreferrer"
          isExternal
          style={labelStyle}
          data-wovn-ignore
        >
          {shinkansenTicketPickupNotice.stationLabel}
        </Link>
      );
    }
    return (
      <strong style={labelStyle} data-wovn-ignore>
        {shinkansenTicketPickupNotice?.stationLabel}
      </strong>
    );
  };

  const sortTransportElementAttachFiles = (
    files: TransportElementAttachFile[],
    travelerInformations: TravelerInformation[],
    userId: number
  ): TransportElementAttachFile[] => {
    return files.slice().sort((a, b) => {
      const aUserIdMatch = travelerInformations.some(
        traveler =>
          traveler.id !== undefined &&
          traveler.id.toString() === a.travelerInformationId &&
          traveler.userId === userId
      );
      const bUserIdMatch = travelerInformations.some(
        traveler =>
          traveler.id !== undefined &&
          traveler.id.toString() === b.travelerInformationId &&
          traveler.userId === userId
      );
      // user_id が一致するファイルを先頭に、それ以外はid順
      if (aUserIdMatch && !bUserIdMatch) return -1;
      if (!aUserIdMatch && bUserIdMatch) return 1;
      return a.id - b.id;
    });
  };

  return (
    <>
      {orderItem.elements.map((element, elementIndex: number) => (
        <MainBlock key={element.id}>
          {!orderItem.isFollowingElement(element) && <BlockTitle>{element.title}</BlockTitle>}
          <BlockContent>
            {element instanceof HotelElement &&
              (element.isCanceled
                ? element.name && (
                    <>
                      <CanceledBlockTextL data-wovn-ignore>{element.name}</CanceledBlockTextL>
                      <CancelMessage>(キャンセル済み)</CancelMessage>
                    </>
                  )
                : element.name && <BlockTextL data-wovn-ignore>{element.name}</BlockTextL>)}
            {element instanceof TransportElement && (element.name || '')}
            <BlockTextM>
              <TranslateIgnoreText text={element.summary} />
            </BlockTextM>
            <BlockTextM>
              {element instanceof HotelElement ? (
                <>
                  <p>
                    返金：
                    {element.refundable === true ? (
                      <span>可</span>
                    ) : element.refundable === false ? (
                      <span>不可</span>
                    ) : (
                      <span>お問い合わせください</span>
                    )}
                  </p>
                  {element.structuredHotelTypeDetail().map((l: TranslatedLine) => {
                    return <TripCardHotelTypeLine key={l.label} line={l} />;
                  })}
                  {element.detailPath && (
                    <BlockTextM>
                      <span>宿泊詳細情報：</span>
                      <span>
                        <a href={element.detailPath} target="_blank" rel="noopener noreferrer">
                          詳細はコチラ
                        </a>
                      </span>
                    </BlockTextM>
                  )}
                  <p>
                    住所:{' '}
                    <Link
                      data-wovn-ignore
                      href={element.hotelMapAddressLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {element.address ? element.address : '所在地を確認'}
                    </Link>
                  </p>
                  <p>
                    {element.tel && (
                      <>
                        <span>電話番号：</span>
                        <span data-wovn-ignore>{toDomesticTel(element.tel)}</span>
                      </>
                    )}
                  </p>
                  <Room>
                    {element.structuredRoomTypeDetail().map((l: TranslatedLine, k: number) => {
                      return <TripCardHotelTypeLine key={`t_${k}`} line={l} />;
                    })}
                  </Room>
                  {/* ホテル予約情報 */}
                  {element.hotelReservationNumber && (
                    <>
                      <span>ホテル予約番号：</span>
                      <span data-wovn-ignore>{element.hotelReservationNumber}</span>
                    </>
                  )}
                  {!hideCancelPolicy && element.displayCancelPolicy && (
                    <div style={styles.cancelPolicyWrapper}>
                      <span>■キャンセルポリシー</span>
                      <div style={styles.cancelPolicyBox} data-wovn-ignore>
                        {element.displayCancelPolicy}
                      </div>
                    </div>
                  )}
                </>
              ) : element instanceof TransportElement ? (
                <>
                  {/* 予約情報 */}
                  {element.ticketNumber && (
                    <BlockTextM>
                      <span>航空券番号：</span>
                      <span data-wovn-ignore>{element.ticketNumber}</span>
                    </BlockTextM>
                  )}
                  {element.airSeatNumber && (
                    <BlockTextM>
                      <span>座席番号：</span>
                      <span data-wovn-ignore>{element.airSeatNumber}</span>
                    </BlockTextM>
                  )}
                  {element.reservationNumber && (
                    <BlockTextM>
                      <span>予約番号：</span>
                      <span data-wovn-ignore>{element.reservationNumber}</span>
                    </BlockTextM>
                  )}
                  {element.exicReceiptNumber && (
                    <BlockTextM>
                      <span>EXICお預かり番号：</span>
                      <span data-wovn-ignore>{element.exicReceiptNumber}</span>
                    </BlockTextM>
                  )}
                  {!orderItem.isPrecedingElement(element) && (
                    <>
                      {element
                        .structuredDetail(orderItem, { elementIndex })
                        .map((l: TranslatedLine, k: number) => {
                          return l.translate ? (
                            <BlockTextM key={`t_${k}`}>
                              <span>{l.label}</span>
                              <span>{l.value}</span>
                            </BlockTextM>
                          ) : (
                            <BlockTextM key={`nt_${k}`}>
                              <span>{l.label}</span>
                              <span data-wovn-ignore>{l.value}</span>
                            </BlockTextM>
                          );
                        })}
                      <Box
                        component="table"
                        width="auto"
                        margin="0 !important"
                        border="none"
                        style={{ verticalAlign: 'top' }}
                      >
                        <tbody>
                          {orderItem.travelerShinkansenTypes(elementIndex) &&
                            orderItem.travelerShinkansenTypes(elementIndex).map((tst, i: number) => (
                              <tr key={i}>
                                {orderItem.travelerShinkansenTypes.length > 1 ? (
                                  <MainTdData>{`${i + 1}人目`}</MainTdData>
                                ) : (
                                  <MainTdData>出張者</MainTdData>
                                )}
                                <MainTd>
                                  <TranslateIgnoreText text={tst} />
                                </MainTd>
                              </tr>
                            ))}
                          {orderItem.travelerAirlineTypes &&
                            orderItem.travelerAirlineTypes.map((tat, i: number) => (
                              <tr key={i}>
                                {orderItem.travelerAirlineTypes.length > 1 ? (
                                  <MainTdData>{`${i + 1}人目`}</MainTdData>
                                ) : (
                                  <MainTdData>出張者</MainTdData>
                                )}
                                <MainTd>
                                  <TranslateIgnoreText text={tat} />
                                </MainTd>
                              </tr>
                            ))}
                        </tbody>
                      </Box>
                      {element.detailPath && (
                        <BlockTextM>
                          <span>宿泊詳細情報：</span>
                          <span>
                            <a href={element.detailPath} target="_blank" rel="noreferrer noopener">
                              詳細はコチラ
                            </a>
                          </span>
                        </BlockTextM>
                      )}
                    </>
                  )}
                </>
              ) : (
                element instanceof WifiElement && (
                  <>
                    {' '}
                    <Box
                      component="table"
                      width="auto"
                      margin="0 !important"
                      border="none"
                      style={{ verticalAlign: 'top' }}
                    >
                      <tbody>
                        {orderItem.travelerShinkansenTypes(elementIndex) &&
                          orderItem.travelerShinkansenTypes(elementIndex).map((tst, i: number) => (
                            <tr key={i}>
                              {orderItem.travelerShinkansenTypes.length > 1 ? (
                                <MainTdData>{`${i + 1}人目`}</MainTdData>
                              ) : (
                                <MainTdData>出張者</MainTdData>
                              )}
                              <MainTd>
                                <TranslateIgnoreText text={tst} />
                              </MainTd>
                            </tr>
                          ))}
                        {orderItem.travelerAirlineTypes &&
                          orderItem.travelerAirlineTypes.map((tat, i: number) => (
                            <tr key={i}>
                              {orderItem.travelerAirlineTypes.length > 1 ? (
                                <MainTdData>{`${i + 1}人目`}</MainTdData>
                              ) : (
                                <MainTdData>出張者</MainTdData>
                              )}
                              <MainTd>
                                <TranslateIgnoreText text={tat} />
                              </MainTd>
                            </tr>
                          ))}
                      </tbody>
                    </Box>
                    {element.reservationNumber && (
                      <BlockTextM>
                        <span>注文番号：</span>
                        <span data-wovn-ignore>{element.reservationNumber}</span>
                      </BlockTextM>
                    )}
                  </>
                )
              )}
              {!orderItem.isPrecedingElement(element) && (
                <>
                  <Space />
                  {element instanceof TransportElement && (
                    <>
                      {theme.themeClass === 'tabikobo' && orderItem.price?.flightPriceChangeDetail && (
                        <p>{orderItem.price.flightPriceChangeDetail}</p>
                      )}
                      {element.isRawTicketingExpired(orderItem) && (
                        <p>
                          発券期限：
                          <Deadline>{element.rawTicketingExpiredAt(orderItem)}</Deadline>
                        </p>
                      )}
                    </>
                  )}
                  {(element instanceof TransportElement || element instanceof HotelElement) &&
                    MarketLogHelper.isElementMarketLogLinkAvailable(element, theme.serviceName, trip) && (
                      <Link
                        href={MarketLogHelper.elementMarketLogPath(element, trip.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        マーケットログを確認
                        <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                          <ExternalLinkIcon />
                        </div>
                      </Link>
                    )}
                </>
              )}
            </BlockTextM>
            <AttachFilesContainer>
              {element instanceof TransportElement &&
                sortTransportElementAttachFiles(
                  element.transportElementAttachFiles || [],
                  orderItem.travelerInformations,
                  user.id
                ).map((transportElementAttachFile: TransportElementAttachFile) => {
                  const attachFileTraveler = orderItem.travelerInformations.find(
                    traveler =>
                      traveler.id !== undefined &&
                      traveler.id.toString() === transportElementAttachFile.travelerInformationId
                  );
                  return (
                    <div key={transportElementAttachFile.id} style={{ flex: '0 1 auto' }}>
                      <BlockTextM style={{ display: 'flex' }}>
                        <PdfIcon style={{ color: '#a72c09', marginRight: '2px' }} />
                        <A
                          href={`/trips/${transportElementAttachFile.tripId}/transport_element_attach_files/${transportElementAttachFile.id}.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {transportElementAttachFile.uploadFileName}
                        </A>
                      </BlockTextM>
                      <AttachFileTraveler>
                        {attachFileTraveler && ` ${attachFileTraveler.name} 様`}
                      </AttachFileTraveler>
                    </div>
                  );
                })}
            </AttachFilesContainer>
            {element instanceof WifiElement && element.attachFiles && element.attachFiles.length > 0 && (
              <AttachFilesContainer>
                {element.attachFiles.map(file => (
                  <div key={file.id} style={{ flex: '0 1 auto' }}>
                    <BlockTextM style={{ display: 'flex' }}>
                      <PdfIcon style={{ color: '#a72c09', marginRight: '2px' }} />
                      <A
                        href={`/trips/${file.tripId}/element_attach_files/${file.id}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {file.uploadFileName}
                      </A>
                    </BlockTextM>
                    <AttachFileTraveler>
                      {file.travelerInformationId &&
                        (() => {
                          const traveler = orderItem.travelerInformations.find(
                            t => t.id !== undefined && t.id.toString() === file.travelerInformationId
                          );
                          return traveler ? `${traveler.name} 様` : '';
                        })()}
                    </AttachFileTraveler>
                  </div>
                ))}
              </AttachFilesContainer>
            )}
          </BlockContent>
        </MainBlock>
      ))}
      {/* JRパッケージ 予約情報 */}
      {orderItem.orderItemCategory === 'jr_package' && (
        <>
          {orderItem.jrAuthCode && (
            <BlockTextM>
              <span>JR認証番号：</span>
              <span data-wovn-ignore>{orderItem.jrAuthCode}</span>
            </BlockTextM>
          )}
          {orderItem.jrPickupCode && (
            <BlockTextM>
              <span>JR引取番号：</span>
              <span data-wovn-ignore>{orderItem.jrPickupCode}</span>
            </BlockTextM>
          )}
        </>
      )}
      {orderItem.eTickets.length > 0 &&
        orderItem.eTickets.map((ticket, k: number) => (
          <p key={k}>
            <a href={ticket.link} target="_blank" rel="noopener noreferrer">
              e-ticketを表示
              <img
                className="organization-trips-detail__detail-panel__item-block__market-log-link-confirm"
                src="/images/external_link_icon.png"
                alt="link"
              />
            </a>
          </p>
        ))}
      {orderItem.price && (
        <MainBlock>
          <span data-wovn-ignore>{utils.digits(orderItem.price.totalPrice)}</span>
          <span>円</span>
        </MainBlock>
      )}
      {shouldShowShinkansenTicketPickupNotice(serviceId) && shinkansenTicketPickupNotice && (
        <>
          <div style={styles.ticketPickupNotice}>
            <p>
              <strong>{shinkansenTicketPickupNotice.title}</strong>
            </p>
            {shinkansenTicketPickupNotice.descriptionLines.map(line => (
              <p key={line}>{line}</p>
            ))}
            {shinkansenTicketPickupNotice.phoneNumber && (
              <>
                <p>
                  <strong>「{shinkansenTicketPickupNotice.phoneNumber}」</strong>をご入力ください。
                </p>
                <br />
              </>
            )}
            <p>
              {shinkansenTicketPickupNotice.withNotePrefix ? '※' : null}
              チケットは「
              {renderPickupStationLabel()}
              」で受け取る必要があります
            </p>
            {shinkansenTicketPickupNotice.bulletLines.map(line => {
              const stationPrefix = `・${shinkansenTicketPickupNotice.stationLabel}`;
              if (line.startsWith(stationPrefix)) {
                return (
                  <p key={line}>
                    ・{renderPickupStationLabel()}
                    {line.slice(stationPrefix.length)}
                  </p>
                );
              }
              return <p key={line}>{line}</p>;
            })}
          </div>
          <BlockTextM>
            <Link
              href={shinkansenTicketPickupNotice.manualUrl}
              target="_blank"
              rel="noopener noreferrer"
              isExternal
              style={{ textDecoration: 'underline' }}
            >
              ・{shinkansenTicketPickupNotice.manualLabel}
            </Link>
          </BlockTextM>
          <Space />
        </>
      )}
      {showOverseas24hSupportNotice && (
        <>
          <div style={styles.ticketPickupNotice}>
            <p>
              <strong>{OVERSEAS_24H_SUPPORT_NOTICE_TITLE}</strong>
            </p>
            {OVERSEAS_24H_SUPPORT_NOTICE_BODY_LINES.map((line, i) =>
              line === '' ? <br key={`overseas-24h-blank-${i}`} /> : <p key={line}>{line}</p>
            )}
          </div>
          <Space />
        </>
      )}
      {businessEkinetTicketlessNotice && (
        <>
          <div style={styles.ticketPickupNotice} data-testid="business-ekinet-ticketless-notice">
            <p>{businessEkinetTicketlessNotice.introLine}</p>
            <p>
              <strong>{businessEkinetTicketlessNotice.icSectionTitle}</strong>
            </p>
            <p>{businessEkinetTicketlessNotice.icBulletLine}</p>
            {businessEkinetTicketlessNotice.icLinks.map(link => (
              <p key={link.url}>
                　
                <Link href={link.url} target="_blank" rel="noopener noreferrer" isExternal>
                  {link.label}
                </Link>
              </p>
            ))}
            {businessEkinetTicketlessNotice.expressSectionTitle && (
              <>
                <p>
                  <strong>{businessEkinetTicketlessNotice.expressSectionTitle}</strong>
                </p>
                <p>{businessEkinetTicketlessNotice.expressOnlyBulletLine}</p>
                <p>
                  {businessEkinetTicketlessNotice.expressAreaBulletPrefix}
                  {businessEkinetTicketlessNotice.expressInlineLink && (
                    <Link
                      href={businessEkinetTicketlessNotice.expressInlineLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      isExternal
                    >
                      {businessEkinetTicketlessNotice.expressInlineLink.label}
                    </Link>
                  )}
                  {businessEkinetTicketlessNotice.expressAreaBulletSuffix}
                </p>
                {businessEkinetTicketlessNotice.expressFooterLink && (
                  <p>
                    　
                    <Link
                      href={businessEkinetTicketlessNotice.expressFooterLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      isExternal
                    >
                      {businessEkinetTicketlessNotice.expressFooterLink.label}
                    </Link>
                  </p>
                )}
              </>
            )}
          </div>
          <Space />
        </>
      )}
    </>
  );
};

const MainTd = styled.td`
  padding: 5px 0;
  border: none;
  vertical-align: top;
  color: ${props => props.theme.grayTextColor}
  font-weight: bold;

  ${media.sp`
    padding: 3px 0;
  `}
`;

const MainTdData = styled(MainTd)`
  width: 60px;
  color: ${props => props.theme.disabledColor};
`;

const MainBlock = styled.div``;

const BlockTitle = styled.p`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.6;
  margin-bottom: 10px;

  ${media.sp`
    font-size: 12px;
  `}
`;

const BlockContent = styled.div`
  line-height: 1.8;
`;

const BlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  ${media.sp`
    font-size: 12px;
  `}
`;

const CanceledBlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-decoration: line-through;
  ${media.sp`
    font-size: 12px;
  `}
`;

const CancelMessage = styled.div`
  color: red;
`;

const BlockTextM = styled.div``;

const Space = styled.div`
  margin-bottom: 10px;
`;

const Room = styled.div`
  margin-top: 20px;
`;

const Deadline = styled.span`
  color: ${props => props.theme.redColor};
`;

const AttachFilesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 5px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const AttachFileTraveler = styled.p`
  font-size: 10px;
  margin-left: 5px;
`;

const getStyles = () => ({
  ticketPickupNotice: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 4,
    padding: 12,
    margin: '10px 0',
    lineHeight: 1.8
  },
  cancelPolicyWrapper: {
    marginTop: '8px',
    marginBottom: '8px'
  },
  cancelPolicyBox: {
    backgroundColor: '#eee',
    padding: '8px 12px',
    borderRadius: '6px',
    lineHeight: 1.6,
    whiteSpace: 'pre-line' as const,
    marginTop: '4px'
  }
});

export default withTheme(OrderItemContent);
